import type { NodeViewProps } from '@tiptap/react';

import { mergeAttributes, Node } from '@tiptap/core';
import '@tiptap/markdown';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';

export const NP_AT_HANDLE_TAG = 'np-at-handle';

export interface MessageMarkdownMentionAttrs {
  handle: string;
}

export interface MessageMentionSuggestionItem {
  id: string;
  handle: string;
  label: string;
}

function escapeHtmlAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function normalizeHandle(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }

  return value
    .trim()
    .replace(/^@+/, '')
    .replace(/\s+/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '')
    .slice(0, 64);
}

export function normalizeMessageMarkdownMentionAttrs(
  attrs: unknown,
): MessageMarkdownMentionAttrs {
  const rec =
    attrs && typeof attrs === 'object'
      ? (attrs as Record<string, unknown>)
      : {};

  return {
    handle: normalizeHandle(rec.handle),
  };
}

export function renderMessageMarkdownMentionTag(handle: string): string {
  const normalized = normalizeHandle(handle);
  if (!normalized) {
    return '';
  }

  return `<${NP_AT_HANDLE_TAG} handle="${escapeHtmlAttr(normalized)}"></${NP_AT_HANDLE_TAG}>`;
}

export function buildMessageMarkdownMentionPreview(markdown: string): string {
  return markdown.replace(
    /<np-at-handle\s+handle="([^"]+)"\s*><\/np-at-handle>/g,
    (_match: string, rawHandle: string) => {
      const handle = normalizeHandle(rawHandle);
      if (!handle) {
        return '';
      }

      return `<span class="np-inline-handle" data-handle="${escapeHtmlAttr(handle)}"></span>`;
    },
  );
}

export function buildMessageMentionSuggestions(
  query: string,
): MessageMentionSuggestionItem[] {
  const handle = normalizeHandle(query);
  if (!handle) {
    return [];
  }

  return [
    {
      id: handle,
      handle,
      label: `@${handle}`,
    },
  ];
}

function MessageMarkdownMentionNodeView({ node }: NodeViewProps) {
  const attrs = normalizeMessageMarkdownMentionAttrs(node.attrs);

  return (
    <NodeViewWrapper
      as="span"
      className="message-markdown-mention"
      contentEditable={false}
      data-drag-handle={false}
      data-handle={attrs.handle}
    >
      <span className="message-markdown-mention__chip">{`@${attrs.handle}`}</span>
    </NodeViewWrapper>
  );
}

export const MessageMarkdownMentionNode = Node.create({
  name: 'npAtHandle',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: false,
  isolating: true,
  defining: true,

  addAttributes() {
    return {
      handle: {
        default: '',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: NP_AT_HANDLE_TAG,
        getAttrs: (element) => {
          if (!(element instanceof HTMLElement)) {
            return false;
          }

          return normalizeMessageMarkdownMentionAttrs({
            handle: element.getAttribute('handle'),
          });
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = normalizeMessageMarkdownMentionAttrs(HTMLAttributes);
    return [NP_AT_HANDLE_TAG, mergeAttributes({ handle: attrs.handle })];
  },

  renderMarkdown(node) {
    const attrs = normalizeMessageMarkdownMentionAttrs(node?.attrs);
    return renderMessageMarkdownMentionTag(attrs.handle);
  },

  addNodeView() {
    return ReactNodeViewRenderer(MessageMarkdownMentionNodeView);
  },
});
